import React, { Suspense, useCallback, useRef, useState, useEffect } from 'react';
import { ResizeObserver } from '@juggle/resize-observer';
import { Canvas, useThree } from '@react-three/fiber';
import { Blob } from './Blob.js';
import { Cubic } from './Cubic.js';
import { Spherical } from './Spherical.js';
import { Polyhedral } from './Polyhedral.js';
import { Floor } from './Floor.js';
import { Platelet } from './Platelet.js';
import { Rod } from './Rod.js';
import { Tube } from './Tube.js';
import { Wires } from './Wires.js';
import { Inconclusive } from './Inconclusive.js';

// Helper component to trigger capture
function CaptureImage({ onCapture }) {
  const { gl, scene, camera } = useThree();



  useEffect(() => {
    // Wait a short delay to ensure everything is rendered
    const timeout = setTimeout(() => {
      gl.render(scene, camera);
      const imgData = gl.domElement.toDataURL('image/png');
      onCapture(imgData);
    }, 100);
    return () => clearTimeout(timeout);
  }, [gl, scene, camera, onCapture]);

  return null;
}

const Visualization = ({
  core,
  coreModification,
  coreColor = "#8cfc42",
  surfaceFunctionalization,
  surfaceFunctionalizationType = 'No modification',
  surfaceFunctionalizationColor = "#8cfc42",
  lineColor = "#8cfc42",
  isNanoformImageGenerated = false,
  visualizationType = 'blob',
  measureScale = 0,
  height = '100%',
  staticImage = false,// 👈 Add this prop
}) => {
  const [image, setImage] = useState(null);

  const renderShape = useCallback((shapeType) => {
    switch (shapeType) {
      case 'cubic': return <Cubic {...{ core, coreModification, surfaceFunctionalization, surfaceFunctionalizationType, coreColor, surfaceFunctionalizationColor, lineColor, isNanoformImageGenerated, measureScale }} baseRotation={[Math.PI / 18, Math.PI / 4, 0]} />;
      case 'spherical': return <Spherical {...{ core, coreModification, surfaceFunctionalization, surfaceFunctionalizationType, coreColor, surfaceFunctionalizationColor, lineColor, isNanoformImageGenerated, measureScale }} baseRotation={[0, 0, 0]} />;
      case 'polyhedral': return <Polyhedral {...{ core, coreModification, surfaceFunctionalization, surfaceFunctionalizationType, coreColor, surfaceFunctionalizationColor, lineColor, isNanoformImageGenerated, measureScale }} baseRotation={[Math.PI / 10, 0, 0]} />;
      case 'platelet': return <Platelet {...{ core, coreModification, surfaceFunctionalization, surfaceFunctionalizationType, coreColor, surfaceFunctionalizationColor, lineColor, isNanoformImageGenerated, measureScale }} baseRotation={[Math.PI / 18, Math.PI / 4, 0]} />;
      case 'rod': return <Rod {...{ core, coreModification, surfaceFunctionalization, surfaceFunctionalizationType, coreColor, surfaceFunctionalizationColor, lineColor, isNanoformImageGenerated, measureScale }} baseRotation={[0, Math.PI / 4, Math.PI / 2]} />;
      case 'tube': return <Tube {...{ core, coreModification, surfaceFunctionalization, surfaceFunctionalizationType, coreColor, surfaceFunctionalizationColor, lineColor, isNanoformImageGenerated, measureScale }} baseRotation={[0, Math.PI / 4, Math.PI / 2]} />;
      case 'wires': return <Wires {...{ core, coreModification, surfaceFunctionalization, surfaceFunctionalizationType, coreColor, surfaceFunctionalizationColor, lineColor, isNanoformImageGenerated, measureScale }} baseRotation={[Math.PI / 18, Math.PI / 4, Math.PI / 2]} />;
      case 'blob':
      default: return <Inconclusive {...{ core, coreModification, surfaceFunctionalization, surfaceFunctionalizationType, coreColor, surfaceFunctionalizationColor, lineColor, isNanoformImageGenerated, measureScale }} baseRotation={[Math.PI / 10, 0, 0]} />;
    }
  }, [core, coreModification, coreColor, surfaceFunctionalization, surfaceFunctionalizationColor, lineColor, isNanoformImageGenerated]);

  return (
    <div
      style={{
        width: '100%',
        height: height,
        position: 'relative',
        // border: '1px solid #b0cda0',
        borderRadius: '5px',
        overflow: 'hidden',
        boxShadow: '0 5px 10px 0 rgba(0, 0, 0, .2)',

      }}
    >
      {staticImage && image ? (
        <img src={image} alt="Visualization" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <Canvas
          style={{ width: '100%', height: '100%' }}
          resize={{ polyfill: ResizeObserver }}
          gl={{ preserveDrawingBuffer: true }}
        >
          <ambientLight castShadow intensity={1.5} color="white" />
          <pointLight position={[0, 5, 10]} color="#fff" />
          <Suspense fallback={null}>
            <Floor />
            {renderShape(visualizationType)}
          </Suspense>
          {staticImage && <CaptureImage onCapture={setImage} />}
        </Canvas>
      )}
    </div>
  );
};

export default Visualization;
